import React, { useState, useEffect } from 'react';
import api from '../services/api';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR');
}

function formatDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getAncienneteLabel(created_at) {
  if (!created_at) return '—';
  const diff = Math.floor((Date.now() - new Date(created_at).getTime()) / 86400000);
  if (diff < 1) return "Aujourd'hui";
  if (diff === 1) return '1 jour';
  if (diff < 7) return `${diff} jours`;
  if (diff < 30) return `${Math.floor(diff / 7)} sem.`;
  return `${Math.floor(diff / 30)} mois`;
}

export default function ParkingPrint({ entries, statuts, responsables, onClose }) {
  const [photos, setPhotos] = useState({});
  const [notes, setNotes] = useState({});
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      const total = entries.length;
      for (let i = 0; i < entries.length; i++) {
        if (cancelled) return;
        const e = entries[i];
        try {
          const [photoRes, notesRes] = await Promise.all([
            e.has_photo ? api.get(`/parking/${e.id}/photo`).catch(() => null) : Promise.resolve(null),
            api.get(`/parking/${e.id}/notes`).catch(() => ({ data: [] })),
          ]);
          if (cancelled) return;
          if (photoRes?.data?.image_data) {
            setPhotos(prev => ({ ...prev, [e.id]: photoRes.data.image_data }));
          }
          setNotes(prev => ({ ...prev, [e.id]: notesRes?.data || [] }));
        } catch { /* ignore */ }
        setProgress(i + 1);
      }
      if (!cancelled) {
        setLoading(false);
        setTimeout(() => window.print(), 500);
      }
    }
    loadAll();
    return () => { cancelled = true; };
  }, [entries]);

  const getStatut = (valeur) => statuts.find(s => s.valeur === valeur);
  const getResponsable = (id) => {
    const r = responsables.find(r => r.id === id);
    return r ? `${r.nom}${r.prenom ? ' ' + r.prenom : ''}` : '—';
  };

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'var(--bg-surface)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <h2 style={{ margin: 0 }}>Préparation de l'impression…</h2>
        <div style={{ width: 300, height: 8, background: 'var(--border-default)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ width: `${(progress / entries.length) * 100}%`, height: '100%', background: '#3b82f6', transition: 'width 0.2s' }} />
        </div>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>{progress} / {entries.length} véhicules chargés</p>
        <button onClick={onClose} style={{ marginTop: 8, padding: '6px 16px', border: '1px solid var(--border-default)', borderRadius: 6, background: 'var(--bg-surface)', cursor: 'pointer' }}>
          Annuler
        </button>
      </div>
    );
  }

  return (
    <div className="parking-print-container">
      <style>{`
        @media screen {
          .parking-print-container { position: fixed; inset: 0; z-index: 9999; background: #fff; overflow-y: auto; padding: 20px; }
          .parking-print-close { position: fixed; top: 16px; right: 16px; z-index: 10000; padding: 8px 16px; background: #ef4444; color: #fff; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; }
        }
        @media print {
          .parking-print-close { display: none !important; }
          .parking-print-page { page-break-after: always; }
          .parking-print-page:last-child { page-break-after: avoid; }
          body * { visibility: hidden; }
          .parking-print-container, .parking-print-container * { visibility: visible; }
          .parking-print-container { position: absolute; left: 0; top: 0; width: 100%; }
        }
        .parking-print-page {
          max-width: 800px; margin: 0 auto 20px;
        }
        .parking-print-fiche {
          padding: 8px 12px; border: 1px solid #e2e8f0; border-radius: 8px; margin-bottom: 6px;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 9px; color: #1e293b;
        }
        .ppf-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 4px; padding-bottom: 4px; border-bottom: 1px solid #e2e8f0; }
        .ppf-plaque { font-size: 13px; font-weight: 800; font-family: monospace; color: #0891b2; }
        .ppf-marque { font-size: 9px; color: #64748b; margin-top: 1px; }
        .ppf-photo { width: 70px; height: 50px; object-fit: cover; border-radius: 4px; border: 1px solid #e2e8f0; }
        .ppf-photo-empty { width: 70px; height: 50px; display: flex; align-items: center; justify-content: center; background: #f8fafc; border-radius: 4px; border: 1px solid #e2e8f0; color: #94a3b8; font-size: 8px; }
        .ppf-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 2px 10px; margin-bottom: 4px; }
        .ppf-field label { display: block; font-size: 7px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0; }
        .ppf-field span { display: block; font-size: 9px; }
        .ppf-statut-dot { display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
        .ppf-section-title { font-size: 8px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.05em; margin: 3px 0 2px; }
        .ppf-note { padding: 2px 6px; margin-bottom: 1px; border-left: 2px solid #3b82f6; background: #f8fafc; border-radius: 0 3px 3px 0; }
        .ppf-note-header { display: flex; justify-content: space-between; font-size: 8px; color: #64748b; margin-bottom: 0; }
        .ppf-note-text { font-size: 8px; }
      `}</style>

      <button className="parking-print-close" onClick={onClose}>✕ Fermer</button>

      {Array.from({ length: Math.ceil(entries.length / 5) }, (_, pageIdx) => (
        <div key={pageIdx} className="parking-print-page">
          {entries.slice(pageIdx * 5, pageIdx * 5 + 5).map(e => {
            const st = getStatut(e.statut);
            const entryNotes = notes[e.id] || [];
            const photo = photos[e.id];

            return (
              <div key={e.id} className="parking-print-fiche">
                <div className="ppf-header">
                  <div>
                    <div className="ppf-plaque">{e.identifiant || '—'}</div>
                    <div className="ppf-marque">{e.marque || ''}</div>
                  </div>
                  {photo ? (
                    <img className="ppf-photo" src={`data:image/jpeg;base64,${photo}`} alt="" />
                  ) : (
                    <div className="ppf-photo-empty">Pas de photo</div>
                  )}
                </div>

                <div className="ppf-grid">
                  <div className="ppf-field">
                    <label>Emplacement</label>
                    <span>{e.allee || '—'}</span>
                  </div>
                  <div className="ppf-field">
                    <label>Concession</label>
                    <span>{e.concession || '—'}</span>
                  </div>
                  <div className="ppf-field">
                    <label>1ère détection</label>
                    <span>{formatDate(e.created_at)}</span>
                  </div>
                  <div className="ppf-field">
                    <label>Ancienneté</label>
                    <span>{getAncienneteLabel(e.created_at)}</span>
                  </div>
                  <div className="ppf-field">
                    <label>Dernière actualisation</label>
                    <span>{formatDateTime(e.updated_at)}</span>
                  </div>
                  <div className="ppf-field">
                    <label>Propriétaire</label>
                    <span>{e.nom_client || '—'}</span>
                  </div>
                  <div className="ppf-field">
                    <label>Responsable</label>
                    <span>{getResponsable(e.responsable_id)}</span>
                  </div>
                  <div className="ppf-field">
                    <label>Statut</label>
                    <span>
                      <span className="ppf-statut-dot" style={{ background: st?.couleur || '#94a3b8' }} />
                      {st?.nom || e.statut || '—'}
                    </span>
                  </div>
                  {e.action_parking && (
                    <div className="ppf-field">
                      <label>Action à mener</label>
                      <span>{e.action_parking}</span>
                    </div>
                  )}
                </div>

                {entryNotes.length > 0 && (
                  <>
                    <div className="ppf-section-title">📝 Notes ({entryNotes.length})</div>
                    {entryNotes.map(n => (
                      <div key={n.id} className="ppf-note">
                        <div className="ppf-note-header">
                          <strong>{n.prenom} {n.nom}</strong>
                          <span>{formatDateTime(n.created_at)}</span>
                        </div>
                        <div className="ppf-note-text">{n.contenu}</div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
